import { useCallback, useEffect, useMemo } from 'react'

import { RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getDefaultMargin } from '@/shared/builder/viewport/utils'
import { formatColumnValue } from '@/shared/form-utils'
import { getSafely } from '@/shared/safely'
import { isEmpty } from '@/shared/utils'
import { useUpdateFormDefaultValue } from '@/components/__project__/hooks/useUpdateFormDefaultValue'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompSliderProps } from './comp'

function formatWithinRange(value: any, min: number, max: number) {
  if (isNaN(Number(value))) {
    return min
  }
  return Math.min(Math.max(Number(value), min), max)
}

function IsWithinRange(value: any, min: number, max: number, step: number) {
  if (isNaN(Number(value))) {
    return false
  }
  return value >= min && value <= max && (value - min) % step === 0
}

export interface CompSliderWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: CompSliderProps) => React.ReactNode
}

export const CompSliderWrapperCore = ({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex },
  form,
  children,
}: CompSliderWrapperProps) => {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { dataSourceTable, formState, setFormState, formErrors, setRequiredFieldStatus } = form

  // ========================= data =========================
  const columnId = settings[ComponentSettingKey.SliderDataValue] as string
  const column = dataSourceTable?.columnsMap?.get(columnId)
  const range = settings?.[ComponentSettingKey.SliderDataRange] as boolean

  const defaultValue = useParseVariableSelect(
    settings[ComponentSettingKey.SliderDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const isEditable = getSafely(settings, [ComponentSettingKey.SliderDataDefaultValueEditable], true)

  const labelData = settings?.[ComponentSettingKey.SliderDataLabel]
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)
  const supportingTextData = settings?.[ComponentSettingKey.SliderDataHelp]
  const supportingText = useParseVariableText(supportingTextData, containerClient, detailTableId, detailRowIndex)

  // ========================= design =========================
  const showTicks = Boolean(settings?.[ComponentSettingKey.SliderDesignTickMark])
  const thickness = getSafely(settings, [ComponentSettingKey.SliderDesignThickness], [4])[0]
  const width = getSafely(settings, [ComponentSettingKey.SliderDesignWidth], [100])[0]
  const showMinMaxValues = Boolean(settings?.[ComponentSettingKey.SliderDesignShowMinMaxValues])
  const showMinMaxLabels = Boolean(settings?.[ComponentSettingKey.SliderDesignShowMinMaxLabels])
  const padding = getSafely(settings, [ComponentSettingKey.SliderDesignPadding], getDefaultMargin())

  // ========================= behavior =========================
  const min = getSafely(settings, [ComponentSettingKey.SliderBehaviorMinValue], 0)
  const max = getSafely(settings, [ComponentSettingKey.SliderBehaviorMaxValue], 100)
  const step = getSafely(settings, [ComponentSettingKey.SliderBehaviorStepValue], 1)
  const required = getSafely(settings, [ComponentSettingKey.SliderBehaviorRequired], false)

  // ========================= form state =========================

  const value = (formState[columnId] as number) ?? null
  const error = formErrors[columnId]

  const defaultValueFormatted = useMemo(() => {
    const formattedValue = formatColumnValue(defaultValue, column)
    return IsWithinRange(formattedValue, min, max, step) ? formattedValue : null
  }, [defaultValue, column, min, max, step])

  useUpdateFormDefaultValue({
    defaultValue: defaultValueFormatted,
    value,
    onUpdate: (defValue) => {
      setFormState((prev) => ({ ...prev, [columnId]: formatColumnValue(defValue, column) }))
    },
  })

  const handleChange = useCallback(
    (value: number) => {
      setFormState((prev) => ({ ...prev, [columnId]: value }))
    },
    [columnId, setFormState]
  )

  // Add effect to update required field status
  useEffect(() => {
    if (required) {
      setRequiredFieldStatus(columnId, isEmpty(value))
    }
    return () => {
      if (required) {
        setRequiredFieldStatus(columnId, false)
      }
    }
  }, [required, value, columnId, setRequiredFieldStatus])

  // ========================= render =========================
  const sliderProps: CompSliderProps = {
    id: component._id,
    label,
    supportingText,
    error,
    disabled: !isEditable,
    required,
    minValue: min,
    maxValue: max,
    minLabel: 'Min',
    maxLabel: 'Max',
    showMinMaxValues,
    showMinMaxLabels,
    showTicks,
    thickness: `${thickness}px`,
    step,
    value,
    onChange: handleChange,
    width: width,
    padding: padding as RawSpacingInput,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(sliderProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(sliderProps)
}
